<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
</svg>

          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              {{ $t('ProjectSettings.UserTab.headers.accessTokens') }}
            </h3>

            <p class="text-sm text-gray-600">
              {{ $t('ProjectSettings.UserTab.messages.accessTokensDescription') }}
            </p>
          </div>
        </div>

        <button
          type="button"
          class="btn btn--primary-blue flex items-center space-x-2"
          @click="showCreateModal = true"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

          <span>{{ $t('ProjectSettings.UserTab.button.createToken') }}</span>
        </button>
      </div>
    </div>

    <div class="p-6">
      <div
        v-if="fetchTokensAction.is(States.LOADING)"
        class="text-center py-12"
      >
        <AppSpinner class="h-8 w-8 mx-auto mb-4" />

        <p class="text-gray-600">
          {{ $t('ProjectSettings.UserTab.messages.loadingTokens') }}
        </p>
      </div>

      <div
        v-else-if="tokens.length === 0"
        class="text-center py-12"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mx-auto h-12 w-12 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
</svg>

        <h3 class="mt-2 text-sm font-medium text-gray-900">
          {{ $t('ProjectSettings.UserTab.messages.noTokens') }}
        </h3>

        <p class="mt-1 text-sm text-gray-500">
          {{ $t('ProjectSettings.UserTab.messages.noTokensHint') }}
        </p>
      </div>

      <div
        v-else
        class="overflow-hidden border border-gray-200 rounded-lg"
      >
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {{ $t('ProjectSettings.UserTab.table.name') }}
              </th>

              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {{ $t('ProjectSettings.UserTab.table.lastUsed') }}
              </th>

              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {{ $t('ProjectSettings.UserTab.table.created') }}
              </th>

              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {{ $t('ProjectSettings.UserTab.table.actions') }}
              </th>
            </tr>
          </thead>

          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="token in tokens"
              :key="token.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ token.name }}
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {{ formatDate(token.last_used_at) }}
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {{ formatDate(token.created_at) }}
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  type="button"
                  class="btn btn--tertiary-blue text-red-600"
                  :disabled="revokeTokenAction.is(States.LOADING)"
                  @click="revokeToken(token)"
                >
                  {{ $t('ProjectSettings.UserTab.button.revokeToken') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Modal
      v-if="showCreateModal"
      size="sm"
      @close="closeCreateModal"
    >
      <template #icon>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
</svg>
      </template>

      <template #header>
        {{ $t('ProjectSettings.UserTab.headers.createToken') }}
      </template>

      <template #body>
        <div
          v-if="!plainTextToken"
          class="space-y-4"
        >
          <label class="block text-sm font-medium text-gray-700">
            {{ $t('ProjectSettings.UserTab.label.tokenName') }}

            <AppInput
              v-model="tokenForm.form.name"
              class="form-input mt-2"
              type="text"
              name="name"
              :error="tokenForm.getErrors('name')"
              :placeholder="$t('ProjectSettings.UserTab.label.tokenNamePlaceholder')"
            />
          </label>
        </div>

        <div
          v-else
          class="space-y-4"
        >
          <p class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
            {{ $t('ProjectSettings.UserTab.messages.tokenShownOnce') }}
          </p>

          <div class="flex items-center space-x-2">
            <div class="w-full">
              <AppInput
                :model-value="plainTextToken"
                class="form-input font-mono text-sm"
                type="text"
                name="plain_text_token"
                readonly
              />
            </div>

            <button
              type="button"
              class="btn shrink-0 transition-colors"
              :class="tokenCopied
                ? 'bg-green-100 text-green-700 border border-green-200 hover:bg-green-100'
                : 'btn--secondary-blue'"
              :aria-label="tokenCopied
                ? $t('ProjectSettings.UserTab.messages.tokenCopied')
                : $t('ProjectSettings.UserTab.button.copyToken')"
              @click="copyToken"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="tokenCopied"
               
                class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 v-else
               
                class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
</svg>
            </button>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <button
            type="button"
            class="btn btn--tertiary-blue"
            @click="closeCreateModal"
          >
            {{ plainTextToken
              ? $t('ProjectSettings.UserTab.button.done')
              : $t('ProjectSettings.UserTab.button.cancel') }}
          </button>

          <button
            v-if="!plainTextToken"
            type="button"
            class="btn btn--primary-blue"
            :disabled="createTokenAction.is(States.LOADING)"
            @click="createToken"
          >
            {{ $t('ProjectSettings.UserTab.button.createToken') }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import { useUserStore } from '@/store/user';
import type { AccessToken } from '@/types/access-token';
import Swal from 'sweetalert2';
import { onBeforeUnmount, onMounted, ref } from 'vue';

const userStore = useUserStore();
const tokens = ref<AccessToken[]>([]);
const showCreateModal = ref(false);
const plainTextToken = ref<string | undefined>(undefined);
const tokenCopied = ref(false);
let copyResetTimeout: ReturnType<typeof setTimeout> | undefined;

const tokenForm = useFormData({
  name: '',
});

const fetchTokensAction = useStoreApiAction(userStore.fetchTokens);
const createTokenAction = useStoreApiAction(userStore.createToken);
const revokeTokenAction = useStoreApiAction(userStore.revokeToken);

const loadTokens = () => {
  fetchTokensAction.request().then((data) => {
    tokens.value = data;
  });
};

const resetCopiedState = () => {
  tokenCopied.value = false;

  if(copyResetTimeout) {
    clearTimeout(copyResetTimeout);
    copyResetTimeout = undefined;
  }
};

onMounted(() => {
  loadTokens();
});

onBeforeUnmount(() => {
  resetCopiedState();
});

const formatDate = (value: string | null) => {
  if(!value) {
    return t('ProjectSettings.UserTab.messages.never');
  }

  return new Date(value).toLocaleDateString();
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  plainTextToken.value = undefined;
  resetCopiedState();
  tokenForm.setData({ name: '' });
  tokenForm.resetErrors();
  tokenForm.resetDirtyStatus();
};

const createToken = () => {
  tokenForm.resetErrors();

  createTokenAction.request(tokenForm.form).then((data) => {
    plainTextToken.value = data.plain_text_token;
    resetCopiedState();
    loadTokens();
  }).catch((error) => {
    if(error?.data) {
      tokenForm.setErrors(error.data);
    }
  });
};

const copyToken = async () => {
  if(!plainTextToken.value) {
    return;
  }

  await navigator.clipboard.writeText(plainTextToken.value);

  tokenCopied.value = true;

  if(copyResetTimeout) {
    clearTimeout(copyResetTimeout);
  }

  copyResetTimeout = setTimeout(() => {
    tokenCopied.value = false;
    copyResetTimeout = undefined;
  }, 2000);
};

const revokeToken = (token: AccessToken) => {
  Swal.fire({
    title: t('ProjectSettings.UserTab.messages.revokeTokenTitle'),
    text: t('ProjectSettings.UserTab.messages.revokeTokenConfirm', { name: token.name }),
    icon: 'warning',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      revokeTokenAction.request(token.id).then(() => {
        loadTokens();

        Swal.fire({
          icon: 'success',
          text: t('ProjectSettings.UserTab.messages.tokenRevoked'),
        });
      });
    }
  });
};
</script>
